import classnames from 'classnames';
import { TmdxAvatar } from '@ds';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { ResourceDetails } from '@lp/features/MultiSuggest/components/MultiSuggestResult/ResourceDetails';
import { ResourceWarningEnum } from '@/api/apis/multi-suggest';
import { getConsoleSerialNumber, getFullName } from '@/utils/helpers';

type ResourcesListProps = {
  isSurgeonRequired: boolean;
  isSuggestedRoute?: boolean;
  ocsWarning?: ResourceWarningEnum;
  specialistWarning?: ResourceWarningEnum;
};

export const ResourcesList = ({
  isSurgeonRequired,
  isSuggestedRoute = false,
  ocsWarning,
  specialistWarning,
}: ResourcesListProps) => {
  const {
    resources: { ClinicalSpecialist: csList, Surgeon: sList, OCSConsole: deviceList },
  } = useRouteContext();

  const hasSurgeon = !!sList.length;
  const hasCS = !!csList.length;
  const hasDevice = !!deviceList.length;

  const showNoSurgeonInfo = !hasSurgeon && (isSuggestedRoute || isSurgeonRequired);
  const isDeviceSuggestedWarning = ocsWarning === ResourceWarningEnum.CandidateNotFound;
  const isCSSuggestedWarning = specialistWarning === ResourceWarningEnum.CandidateNotFound;

  return (
    <div className="flex flex-col gap-1">
      {!hasCS && (
        <div className="flex gap-1 items-center">
          <TmdxAvatar radius="xl" size="sm" type="clinical-specialist" />
          <span className="text-s-r text-danger-20">No CS found</span>
        </div>
      )}
      {hasCS &&
        csList.map((i) => (
          <div key={i.resourceId} className="flex gap-1 items-center">
            <TmdxAvatar
              radius="xl"
              size="sm"
              type="clinical-specialist"
              name={getFullName(i.data?.firstName, i.data?.lastName)}
              src={i.data?.profilePhoto}
            />
            <ResourceDetails
              isMissingResource={isCSSuggestedWarning}
              missingResourceTitle="No CS found"
              resourceTitle={`${getFullName(i.data?.firstName, i.data?.lastName)}, ${i.data?.baseLocation?.code} (CS)`}
            />
          </div>
        ))}
      {showNoSurgeonInfo && (
        <div className="flex gap-1 items-center">
          <TmdxAvatar radius="xl" size="sm" type="retrieval-surgeon" />
          <span className={classnames('text-s-r', isSurgeonRequired ? 'text-danger-20' : 'text-base-10')}>
            {isSurgeonRequired ? 'No RS found' : 'No RS required'}
          </span>
        </div>
      )}
      {hasSurgeon &&
        sList.map((i) => (
          <div key={i.resourceId} className="flex gap-1 items-center">
            <TmdxAvatar
              radius="xl"
              size="sm"
              name={getFullName(i.data?.firstName, i.data?.lastName)}
              src={i.data?.profilePhoto}
              type="retrieval-surgeon"
            />
            <span className="text-s-r text-base-10">{`${getFullName(i.data?.firstName, i.data?.lastName)} (RS)`}</span>
          </div>
        ))}
      {!hasDevice && (
        <div className="flex gap-1 items-center">
          <TmdxAvatar radius="xl" size="sm" type="device" />
          <span className="text-s-r text-danger-20">No Device found</span>
        </div>
      )}
      {hasDevice &&
        deviceList.map((i) => (
          <div key={i.resourceId} className="flex gap-1 items-center">
            <TmdxAvatar radius="xl" size="sm" type="device" src={i.data?.logoPath} />
            <ResourceDetails
              isMissingResource={isDeviceSuggestedWarning}
              missingResourceTitle="No Device found"
              resourceTitle={`${getConsoleSerialNumber(i.data)}, ${i.data?.hub?.code}`}
            />
          </div>
        ))}
    </div>
  );
};
